import { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Left slide-out help — a detailed, self-serve guide to every part of the app.
 * Context-aware: the page you're on opens scrolled to its section. Written for
 * agency staff (not engineers), so it explains what each thing means and how to
 * act on it.
 */

interface HelpPoint {
  h: string;
  d: string;
}
interface HelpSection {
  id: string;
  title: string;
  blurb: string;
  points: HelpPoint[];
}

/** Map a route path to its help section id. */
export function helpSectionForPath(pathname: string): string {
  if (pathname === '/' || pathname === '') return 'dashboard';
  if (pathname.startsWith('/files')) return 'files';
  if (pathname.startsWith('/jobs')) return 'jobs';
  if (pathname.startsWith('/members') || pathname.startsWith('/policies')) return 'journey';
  if (pathname.startsWith('/compare')) return 'compare';
  if (pathname.startsWith('/audit')) return 'audit';
  if (pathname.startsWith('/chargebacks')) return 'chargebacks';
  if (pathname.startsWith('/medicare')) return 'medicare';
  if (pathname.startsWith('/book-value')) return 'book-value';
  if (pathname.startsWith('/unpaid')) return 'unpaid';
  if (pathname.startsWith('/tax-1099')) return 'tax1099';
  if (pathname.startsWith('/rates')) return 'rates';
  if (pathname.startsWith('/carrier-setup')) return 'setup';
  if (pathname.startsWith('/ask')) return 'assistant';
  if (pathname.startsWith('/profile')) return 'profile';
  return 'overview';
}

const SECTIONS: HelpSection[] = [
  {
    id: 'overview',
    title: 'Getting started',
    blurb:
      'CommissionSight reads your carrier commission statements, tracks every member month to month, and shows you exactly who stayed, changed, or dropped — plus how much commission you’re owed and every chargeback. The loop is simple: upload a statement → we score it in seconds → you act on the results.',
    points: [
      {
        h: 'The 3 steps',
        d: '1) Upload a monthly statement on the Files page (we onboard each carrier for you). 2) We map it, compare it to prior months, and score every member 🟢🟡🔴. 3) Review the dashboard, drill into results, compare periods, and export.',
      },
      {
        h: 'What “a member” means',
        d: 'We track the covered life (the “heartbeat”) using the carrier’s own member or policy ID — never a guess — so the numbers hold up in an audit. One person across two carriers is matched by that ID.',
      },
      {
        h: 'Where to start',
        d: 'New here? Go to Files and upload your most recent statement, then open the Dashboard. Already loaded a few months? The Dashboard and Compare pages are where the insight lives.',
      },
    ],
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    blurb:
      'Your book at a glance for the selected carrier, period, and workspace. The two hero cards are the money: what you’re owed, and what’s at risk this month.',
    points: [
      {
        h: 'Commission owed (recoverable)',
        d: 'Your contracted rate (set on the Rates page) minus what the carrier actually paid — the recoverable shortfall, down to the member. Only policies with a configured rate are evaluated; the line shows that coverage so it’s never a black box.',
      },
      {
        h: 'Commission at risk this period',
        d: 'Month-over-month variance flagged for review: commission from members who dropped (red) plus members paid less than last month (yellow). It’s a “look here” signal, not a final owed figure.',
      },
      {
        h: 'Status mix & attrition',
        d: 'Green = unchanged, yellow = changed, red = dropped. Attrition is the share of members who dropped vs. the prior period. The charts show both trended over the last 12 months.',
      },
      {
        h: 'Statement quality',
        d: 'Flags carriers with abnormal month-over-month movement (e.g. mass drop + mass new at once) — usually a sign a statement is incomplete or the wrong file was uploaded. Check it before acting on big swings. Click a flagged carrier to open the member-level comparison behind the flag.',
      },
      {
        h: 'Filters',
        d: 'Narrow everything by carrier and period; if your account has workspaces, a Workspace filter scopes the whole dashboard to one workspace. “Review members” jumps to the results grid pre-filtered to the at-risk members.',
      },
    ],
  },
  {
    id: 'files',
    title: 'Files & uploading',
    blurb:
      'Upload each carrier’s monthly statement here. CSV or spreadsheet, any layout — we handle the mapping.',
    points: [
      {
        h: 'Uploading',
        d: 'Pick the carrier, the statement period (year + month), and — if workspaces are on — the workspace, then drop the file. You’ll get a confirmation instantly while it processes in the background (usually seconds).',
      },
      {
        h: 'Formats',
        d: 'CSV and Excel (.xlsx) are supported, in whatever column layout your carrier sends. We detect the format from the file itself. If a carrier sends a new layout, contact us and we’ll map it.',
      },
      {
        h: 'Re-uploading / corrections',
        d: 'Uploading a corrected file for a period you’ve already loaded replaces it cleanly (no leftover rows from the old version), and re-scores the following month automatically.',
      },
      {
        h: 'Rejected rows (exceptions)',
        d: 'If some rows can’t be read (e.g. missing a member/policy ID), the rest still load and you get a downloadable exception file listing exactly which rows failed and why, so you can fix and re-upload.',
      },
      {
        h: 'After upload',
        d: 'Each upload becomes a job (see Jobs). When it finishes you get an email with the scored stats — members, status mix, commission owed, and chargebacks.',
      },
      {
        h: 'SFTP auto-ingest (enterprise)',
        d: 'If SFTP is enabled for your account, the “SFTP ingestion” card on this page lets carriers (or your systems) drop statement files into a secure folder and we ingest them automatically — no manual upload. It’s key-based and hands-off once set up.',
      },
      {
        h: 'Email ingestion (enterprise)',
        d: 'If email ingestion is enabled, the “Email ingestion” card gives each carrier a unique inbound address. Forward their statement emails there (or have the carrier send them) and we ingest the attachment automatically — the address itself routes it to the right carrier. Only .csv/.xlsx/.xls attachments are accepted; an optional filename rule reads the period (a statement we can’t place is held for review, never guessed). You can restrict an address to the carrier’s domain and pause or remove it any time.',
      },
      {
        h: 'SFTP — connecting',
        d: 'The card shows your Host, Port, and Username. Paste your SSH public key (ed25519 or RSA — the .pub file, never your private key) to authorize your machine, then connect with the matching private key (key auth, no passwords). Drop files into the folder path shown (“Upload into folder”).',
      },
      {
        h: 'SFTP — filename rules',
        d: 'Each rule maps a filename to a carrier with a regex, and reads the statement period from named groups (?<year>\\d{4}) and (?<month>\\d{2}) — e.g. ^aetna_(?<year>\\d{4})-(?<month>\\d{2})\\.csv$. A file that matches no rule is skipped; one whose month can’t be determined is left unprocessed (it won’t guess).',
      },
      {
        h: 'SFTP — pickup & monitoring',
        d: 'We sweep your folder automatically about every 15 minutes, or click “Scan inbox now” to pick up immediately. Each new file becomes a job and is scored exactly like an upload. A file is never ingested twice; picked-up files are archived out of the inbox, and anything that failed shows in “Recent pickups” with the reason.',
      },
    ],
  },
  {
    id: 'jobs',
    title: 'Jobs & results',
    blurb:
      'Every upload is a job. Open a completed job to see the scored results grid for that statement.',
    points: [
      {
        h: 'Job status',
        d: 'Queued → processing → completed (or failed, with the reason). A completed job links to its results.',
      },
      {
        h: 'The results grid',
        d: 'Every member scored for the period: status pill (🟢🟡🔴), what changed (flags), and the commission — with the prior month for comparison. Filter by status, search by name/member ID/policy, and toggle the “owed” view to see underpaid members.',
      },
      {
        h: 'Reading the colors',
        d: 'Green = present and unchanged. Yellow = present but something changed (commission, a tracked field, or they reappeared). Red = was here last period, now gone. See the Glossary for every flag.',
      },
      {
        h: 'Export',
        d: 'Export any grid to CSV — the rows behind the numbers, ready for a spreadsheet or to take to the carrier.',
      },
    ],
  },
  {
    id: 'journey',
    title: 'Member & policy journey',
    blurb:
      'Click any member or policy to see their full timeline across every statement you’ve loaded.',
    points: [
      {
        h: 'The timeline',
        d: 'Every period the member appears, in order — first-seen, commission each month, what changed, and the exact statement file each data point came from.',
      },
      {
        h: 'Drop-offs & reappearances',
        d: 'If a member drops, the timeline shows the period they went missing and the last statement they were on. If they come back months (or years) later, it’s flagged as reappeared — with the commission delta if it changed.',
      },
      {
        h: 'Why it matters',
        d: 'This is your audit trail for a single life: when you got paid, when you stopped, and the source document for each — exactly what you need to question a carrier.',
      },
    ],
  },
  {
    id: 'compare',
    title: 'Compare periods',
    blurb:
      'See precisely who stayed, changed, dropped, or joined between any two months — across any carrier.',
    points: [
      {
        h: 'Pick two periods',
        d: 'Choose a baseline (“from”) and a comparison (“to”) month, optionally one carrier (or all). Quick-compare buttons set month-over-month, quarter, or year automatically.',
      },
      {
        h: 'Read the summary',
        d: 'Counts for retained / changed / dropped / new / reappeared, then a searchable, filterable row-by-row breakdown with the commission for each period side by side.',
      },
      {
        h: 'Workspaces',
        d: 'If enabled, scope the comparison to one workspace so two workspaces sharing a carrier+period stay separate.',
      },
      { h: 'Export', d: 'Export the full comparison to CSV for reporting.' },
    ],
  },
  {
    id: 'audit',
    title: 'Audit & cumulative totals',
    blurb:
      'The recoverable picture across a span of months, not just one. Audit adds up commission owed, at-risk, and chargebacks over a date range you choose — month by month and per carrier — so you have one defensible number to take to a carrier audit.',
    points: [
      {
        h: 'What it’s for',
        d: 'Compare answers “what changed between two months.” Audit answers “how much were we owed across this whole stretch?” Pick a range and it totals every month’s owed, at-risk, and chargebacks, with a breakdown by carrier and by month.',
      },
      {
        h: 'Pick a range (or the whole book)',
        d: 'Set a From and To month, or tap a quick range — All time, Year to date, Last 12 / 24 months. Leave it open to cover every period you’ve loaded. The carrier and (if enabled) workspace filters scope the totals.',
      },
      {
        h: 'Commission owed — the headline',
        d: 'Each month’s recoverable shortfall (your contracted rate minus what the carrier actually paid), summed across the range. Because every month’s figure is that month’s expected-minus-actual, adding them up is the true total you’re owed over the window.',
      },
      {
        h: 'Owed coverage — always read it next to the owed number',
        d: 'Coverage is how much of the book that owed figure is based on: of every policy we examined, the share we could actually price because it had a rate set (and a premium, for % rates). Shown as a % and as “X of Y records.” The rest aren’t counted as $0 — they’re simply not evaluated, so 60% coverage means the real owed can only go up as you set more rates. Raise it on the Rates page.',
      },
      {
        h: 'At risk & chargebacks',
        d: 'At risk is the month-over-month variance flagged for review (dropped members’ prior commission plus reductions), summed over the range — a “look here” signal, not a final owed amount. Chargebacks is the total the carriers clawed back across the range, with the count.',
      },
      {
        h: 'Members vs. member-months',
        d: 'Member-months is a volume — each month’s member count added together (a year on a ~1,000-member book is ~12,000), not a head-count. For the size of the book, read Avg members / mo and Peak members instead.',
      },
      {
        h: '“Includes estimated rates”',
        d: 'If the owed total leans on a rate we auto-seeded as an estimate (because you haven’t confirmed one yet), the card says so — treat the dollars as approximate until you set your contracted rate, then they firm up.',
      },
      {
        h: 'Export',
        d: 'Export the month-by-month table to CSV for your audit working papers — owed, at risk, chargebacks, coverage, and member movement per period.',
      },
    ],
  },
  {
    id: 'chargebacks',
    title: 'Chargebacks',
    blurb:
      'Every clawback, traced to the dollar it reverses. When a carrier takes commission back, we make it a first-class event — not a line buried in a statement.',
    points: [
      {
        h: 'Original payout (record 0)',
        d: 'For each chargeback we find the policy’s first positive payout — when the carrier first paid it, how much, and from which file — so you see exactly what’s being reversed.',
      },
      {
        h: 'Fully reversed',
        d: 'We flag whether the clawback fully reverses the original payout (full) or only part of it (partial).',
      },
      {
        h: 'Never paid',
        d: 'If there’s a chargeback with no matching original payout, it’s flagged — money taken back that the carrier may never have actually paid you. That’s a conversation to have with the carrier.',
      },
      { h: 'Filters', d: 'Filter by carrier, period, and (if enabled) workspace.' },
      {
        h: 'Proof-out (enterprise) — your whole history at once',
        d: 'The Proof-out tab runs the same trace across EVERY chargeback you’ve ever loaded, not one month at a time. Each line gets a verdict — paid first (with the month, amount, and statement file as evidence), paid only after, or never paid — plus the policy’s lifetime paid vs. clawed-back totals, so a carrier that took back more than it ever paid jumps right out.',
      },
      {
        h: 'Proof-out — export & honesty rules',
        d: 'Export CSV gives you the whole report in one carrier-ready file. Verdicts are only as complete as your loaded statements: “never paid” means no payment in the data we have — if a month is missing, upload it and the report recomputes. Leave the range open to cover everything.',
      },
      {
        h: 'Payment gaps (enterprise) — months they never paid',
        d: 'The Payment gaps tab is the proof-out’s mirror: months a policy was paid before and after but has NO record in between. Runs the carrier later trued up with a lump payment are marked “caught up” ($0); the rest is commission with no evidence it was ever paid, priced conservatively. The page opens on the last 60 days — one click scans your full history.',
      },
      {
        h: 'Or just ask',
        d: 'With these features enabled, Ask AI answers questions like “were my chargebacks actually paid first?” or “which months did the carrier never pay me?” — computed by these same reports, with a link back to the full page.',
      },
    ],
  },
  {
    id: 'tax1099',
    title: '1099 reconciliation',
    blurb:
      'At tax time, check that each carrier’s 1099 matches what they actually paid you. We tally the commission per carrier across the tax year (net of chargebacks) from the statements you loaded; you enter the 1099 box amount; we flag the variance.',
    points: [
      {
        h: 'We tally · you enter',
        d: 'Pick the tax year and we sum the net commission each carrier paid you across that year’s statements. Type the amount from each carrier’s 1099 in the box — the variance (1099 minus our tally) appears instantly, green when it matches, red when it’s off.',
      },
      {
        h: 'Why a mismatch matters',
        d: 'A 1099 that’s higher than what you were actually paid means you’d be taxed on income you never received; lower means the carrier under-reported. Either way it’s a conversation to have with the carrier — and your accountant — with the per-statement trail to back you up.',
      },
      {
        h: 'Load every month first',
        d: 'The tally only counts statements you’ve loaded. If a month is missing, “we tallied” reads low — load the missing statements before comparing, so it’s apples-to-apples.',
      },
      {
        h: 'Export',
        d: 'Download the per-carrier tally, your entered 1099s, and the variance as CSV for your records.',
      },
    ],
  },
  {
    id: 'unpaid',
    title: 'Unpaid members',
    blurb:
      'The biggest find-money check: members you KNOW are active that the carrier never paid you for. Upload your active-member roster and we compare it to who the carrier actually paid on the statement. Your roster is reconciled in memory and never stored.',
    points: [
      {
        h: 'Upload your roster',
        d: 'Export your active members from your AMS/CRM as a CSV and drop it in. Pick which column holds the member ID (we guess it for you); name and plan are optional. Then choose the carrier and statement period to compare against.',
      },
      {
        h: 'Estimated recoverable',
        d: 'The roster members with no paid record this period — money the carrier owes you — times what similar members were actually paid (the median paid commission). It’s an estimate to take to the carrier, grounded in your own statement.',
      },
      {
        h: 'Roster matched',
        d: 'How much of your roster the carrier did pay. A low match rate means either the carrier missed a lot of members (recoverable) or your roster is for a different carrier/period — check the carrier and period first.',
      },
      {
        h: 'Members not in your roster',
        d: 'The flip side: members the carrier paid that aren’t on your roster — usually a stale roster, or members you forgot you hold. Worth a look, but not money owed.',
      },
      {
        h: 'Export',
        d: 'Download the unpaid list as CSV to research each member and build your recovery request to the carrier.',
      },
      {
        h: 'How this differs from Payment gaps',
        d: 'Payment gaps (under Chargebacks) finds months skipped MID-STREAM — a member paid before and after with silence in between — using only your statements. But a member who never appears on any statement leaves no timeline to check, and one whose payments simply stop looks like churn. Those are exactly what this report catches, which is why it’s the one that asks for your roster: it’s the outside ground truth the statements can’t provide.',
      },
    ],
  },
  {
    id: 'book-value',
    title: 'Book value & retention',
    blurb:
      'What your whole book is worth — the annual recurring commission it generates — and how each enrollment class holds up. “All carriers” gives you one whole-book total; pick a carrier to drill into its run-rate, by-state split, and cohort retention.',
    points: [
      {
        h: 'Whole-book value (all carriers)',
        d: 'One total across every carrier, each valued by the model correct for its product line: Medicare by its annual anniversary renewal at CMS rates, ACA / ancillary by monthly run-rate × 12. Both produce an annual recurring figure, so they add up to a single “what’s my book worth” number, with a per-line and per-carrier breakdown.',
      },
      {
        h: 'Complete periods only',
        d: 'Each carrier is valued on its latest COMPLETE statement period. If the most recent month looks partial — a half-loaded statement with far fewer members than the recent norm — it’s skipped, so a missing or partial upload can’t understate your book. The “As of” column shows exactly which period each carrier was valued on.',
      },
      {
        h: 'Gross vs. attrition-adjusted',
        d: 'Annual recurring is the value if the book is fully retained. Attrition-adjusted decays it by the observed month-over-month churn (ACA); Medicare renewal is retention-assumed, with the RDE radar as its downside.',
      },
      {
        h: 'Monthly cash curve',
        d: 'The annual total is comparable across lines, but the timing isn’t — Medicare is front-loaded to renewal anniversaries (often a big January), while ACA is level monthly. The curve shows the combined cash by month so you can plan around it.',
      },
      {
        h: 'Retention by enrollment class',
        d: 'Drill into a carrier to see how each enrollment year retains and how much of the churn lands in the Open-Enrollment window (the OEP bleed, Jan–Mar) — where retention outreach pays off most.',
      },
      {
        h: 'What’s enabled',
        d: 'Medicare carriers appear when the Medicare lifecycle feature is on; ACA / ancillary carriers when Book value is on. The whole-book total covers whichever lines your account has enabled.',
      },
    ],
  },
  {
    id: 'medicare',
    title: 'Medicare lifecycle (RDE radar)',
    blurb:
      'Medicare commission is advanced, then clawed back in full if a member disenrolls inside the first ~3 months (a “rapid disenrollment,” RDE). This page makes that exposure visible — what’s already been clawed back, who’s still inside the window, and which plans and producers churn early.',
    points: [
      {
        h: 'Clawed back this period',
        d: 'The commission CMS took back this period from members who left inside the RDE window — the money that’s already gone. Count = how many rapid disenrollments were reversed.',
      },
      {
        h: 'At risk in the RDE window',
        d: 'Active members still inside the first ~3 months of their enrollment. If any of them disenroll now, the full advance claws back — so this is exposure you can see coming, not a loss yet.',
      },
      {
        h: 'Rapid-disenroll rate',
        d: 'Of everyone who disenrolled this period, the share who left inside the RDE window. A high rate points at a plan or producer writing business that doesn’t stick.',
      },
      {
        h: 'By plan / by writing agent',
        d: 'Where the rapid disenrollments concentrate — which products and which producers churn early, with the dollars clawed back. Use it to coach producers or rethink a plan.',
      },
      {
        h: 'Honest coverage',
        d: 'RDE needs both an effective date and a disenrollment/term date on the statement. Records missing either are counted but never guessed — the coverage line tells you how much of the book the figures reflect.',
      },
    ],
  },
  {
    id: 'rates',
    title: 'Rates — what you’re owed',
    blurb:
      'Tell us what each carrier is contracted to pay. We compare it to what they actually paid to compute commission owed. Two layers: a simple per-carrier rate, and detailed product-line schedules.',
    points: [
      {
        h: 'Simple expected rates',
        d: 'Set a carrier’s contracted rate as a % of premium (per policy) or a flat amount per member. Optionally override per plan code. This drives “commission owed” on the dashboard.',
      },
      {
        h: 'Product-line schedules',
        d: 'For Medicare and ancillary, use the structured editor (no spreadsheets): pick the product line, scope it to a workspace/carrier, and fill in the table — Medicare CMS amounts by year/product/region, or ancillary policy-year rate bands. Add or remove rows as needed.',
      },
      {
        h: 'Ancillary rates by issue-age & state',
        d: 'Supplemental products (Medicare Supplement above all) often pay a different % by state × issue-age (under-65 vs 65+) × product. In the ancillary editor choose “By state / issue-age / product” to build a rate matrix: one cell per state/age/product, each with its own policy-year bands (e.g. WI 65+ years 1–6 = 23.5%, year 7+ = 3.25%). The most specific matching cell wins; a policy that matches no cell is left out of owed (it lowers coverage) rather than mis-scored. Policy year is figured from each policy’s effective date — you don’t enter it. Age comes from the member’s DOB when present, else the plan name (e.g. disability “DIS” plans = under 65).',
      },
      {
        h: 'Medicare rates vary by state',
        d: 'CMS sets a national maximum plus higher caps for a few states/territories (CA, NJ, CT, PA, DC, PR, VI). In the Medicare table, leave Region blank for the national (US) rate, or type a 2-letter state code for a state-specific cap — each member is matched to their state from the statement, falling back to national if the statement has no state or the state has no row. Renewals are 50% of the initial-year amount. You don’t set any of this by hand: the published CMS maximum rates (national + the higher-cap states, recent years) are built in — set a rate here only to override them with your actual contracted amount.',
      },
      {
        h: 'Medicare owed works day-one + an over-cap compliance check',
        d: 'For Medicare carriers you don’t have to enter rates first — commission owed is computed immediately from the built-in CMS maximum rates, shown as an estimate until you confirm your contracted rate (a carrier may pay below the cap, which lowers the true owed; the built-in figures are from public CMS guidance and refreshed each year). CommissionSight also flags any commission paid ABOVE the CMS maximum — a compliance/recoupment exposure, or a sign an amount was mis-mapped — so you catch over-payments, not just shortfalls.',
      },
      {
        h: 'Medicare base payment cadence',
        d: 'Set Base payment cadence to match how your carrier pays the CMS base: “Annual upfront” (one lump — the default and most common) or “Monthly” (12 equal installments). If a carrier pays monthly but it’s left on Annual upfront, owed is overstated every period (the full annual amount is expected against a single month’s payment). Unsure? Leave it Annual upfront and check with the carrier.',
      },
      {
        h: 'Graded health comp — tiers, PMPM bands & production bonuses',
        d: 'Group and individual health rarely pay one flat percentage. A major-medical schedule can grade the rate by premium size (e.g. 5% up to $500 PEPM, 3% above), pay a PMPM (per-member-per-month) that steps by band, split first-year vs renewal business, and add a production-bonus override on top — so a bonus you were owed but not paid shows up as owed. The most specific tier wins, and owed is figured per member.',
      },
      {
        h: 'Major medical vs. Medicare vs. ancillary',
        d: 'Major medical = % of premium / flat per member (with optional graded tiers, PMPM bands & bonuses above). Medicare = CMS advance-and-earn (the maximum-compensation table by contract year). Ancillary = premium-based with first-year/renewal bands. Pick the one that matches the carrier.',
      },
      {
        h: 'When changes apply',
        d: 'Saving a rate re-scores your already-processed periods so the dashboard’s owed totals catch up. A Medicare/ancillary carrier with no schedule can’t compute owed — so set one.',
      },
    ],
  },
  {
    id: 'setup',
    title: 'Set up a carrier (statement-first)',
    blurb:
      'Don’t map columns by hand — don’t even pick a carrier. Drop in a sample statement and we detect who it’s from and take it from there.',
    points: [
      {
        h: 'How it works',
        d: 'Upload a sample statement. If we recognize the carrier and it’s already set up, you’re done — go upload on Files. If we know the carrier but not this format, one click teaches the AI. You’ll see how many sample rows it recognized.',
      },
      {
        h: 'Carrier not supported yet?',
        d: 'If the statement is from a carrier we don’t have, name it and hit “Request this carrier.” We add it — usually within a day — and email you when it’s ready. Track your requests at the bottom of the page.',
      },
      {
        h: 'Your data stays private',
        d: 'Only the column headers and masked sample values (digits and letters hidden) reach the AI or our review queue — never member data.',
      },
      {
        h: 'Often you don’t even need this',
        d: 'A new statement format from a carrier we already support usually onboards itself on first upload (the format is learned automatically). Use this page to confirm up front, or to request a brand-new carrier.',
      },
      {
        h: 'Prefer to pick manually?',
        d: 'The manual carrier picker is still there under “Prefer to pick the carrier manually?” — same AI setup, you just choose the carrier yourself.',
      },
    ],
  },
  {
    id: 'assistant',
    title: 'Ask AI',
    blurb:
      'Ask AI is more than a chat with your book. In one place, in plain English, it answers questions about your data, explains how to use CommissionSight, and does the heavy setup for you — every answer written by a frontier AI model.',
    points: [
      {
        h: '1. Ask about your book',
        d: 'Type the way you’d ask a teammate: “How many members did Humana lose last month, and what were they worth?” Every answer comes with the underlying rows (and the read-only query) so it’s auditable. It’s read-only and scoped to your own data — it can never change anything.',
      },
      {
        h: 'Fluent in every report',
        d: 'Ask about chargebacks, payment gaps, statement coverage, unpaid members, your book summary, carrier scorecards, attrition, owed history, statement quality, Medicare clawback risk and renewal forecasts, book value, cohort retention, 1099 tallies, or what changed between two months — answered by the same engines as those report pages, with the matching rows and a link to the exact filtered view. (Gated reports need the matching feature.)',
      },
      {
        h: 'Watch it work, pick up where you left off',
        d: 'Answers stream in real time — each stage and (on investigations) each step as it completes. Conversations are remembered across refreshes and devices, stored in your own isolated database; “+ New chat” starts a fresh thread.',
      },
      {
        h: 'It speaks first — statement briefs',
        d: 'Every processed statement gets an AI-written brief: what the month looked like, what changed, what needs attention. It tops the completion email and is pinned here on the Ask AI screen.',
      },
      {
        h: 'Watch a question',
        d: 'Click “Watch this” on any recovery answer and the question re-runs automatically after every statement — you get an email only when the matching count grows. Your watches appear as chips on this screen (up to 10).',
      },
      {
        h: 'What the assistant knows',
        d: 'Before answering, it reads your account context: auto-detected coverage warnings (a missing or partial statement month) plus notes on your account. Add your own in the “What the assistant knows” panel — “our Aetna book moved agencies in May” — and it takes effect on the next question.',
      },
      {
        h: 'Attach a statement',
        d: 'Drop a file into the chat (paperclip) and hear what it is BEFORE anything is ingested: the carrier it matches, whether the layout fits the mapping on file, and which months you already have. Confirm carrier + month inline to ingest through the normal pipeline — the brief lands back in the same conversation.',
      },
      {
        h: 'Investigate deeper',
        d: 'One click turns a quick answer into a multi-step investigation: several read-only queries and reports, coverage cross-checked before conclusions, with a visible trace of everything it checked. Runs on your own key when BYOK is set.',
      },
      {
        h: '2. Ask how it works',
        d: '“What does owed coverage mean?” or “How do I set up a rate schedule?” are answered from the product docs, with links to read more.',
      },
      {
        h: '3. Have it set things up',
        d: 'Ask it to set up a new carrier or draft your rates and it points you to the AI setup flows: Set up a carrier (upload a sample → AI maps the columns) and Draft rates with AI on the Rates page. You always review before anything goes live.',
      },
      {
        h: 'Save & schedule reports',
        d: 'Turn any book answer into a saved report (the floppy-disk “Save as report” link), re-run it any time from the chips on this screen, and have it emailed to you weekly or monthly.',
      },
      {
        h: 'Bring your own key — every account',
        d: 'Add your own OpenAI/Anthropic key under Profile → AI key to run Ask AI on your own provider and model, billed by them, with no monthly cap. The assistant is frontier-quality either way — BYOK is about control.',
      },
    ],
  },
  {
    id: 'profile',
    title: 'Profile, team & billing',
    blurb: 'Manage your account details, teammates, payment method, and AI settings.',
    points: [
      {
        h: 'Team',
        d: 'Invite teammates by email (they sign in with a one-time code — no passwords) and remove them instantly. Every invite/removal is recorded in your activity log.',
      },
      {
        h: 'Billing',
        d: 'Set your contact details and payment method (card or bank/ACH). The next-invoice preview shows members × your per-member price plus the processing fee — ACH is cheaper on larger invoices.',
      },
      {
        h: 'Bring your own AI key',
        d: 'If enabled for your account, add an OpenAI-compatible or Anthropic key so Ask AI runs on your provider. The key is stored encrypted and never shown again.',
      },
      { h: 'Appearance', d: 'Toggle light/dark mode from the account menu.' },
    ],
  },
  {
    id: 'glossary',
    title: 'Glossary — statuses & flags',
    blurb: 'Exactly what each color and flag means.',
    points: [
      {
        h: '🟢 Green',
        d: 'The member was present and unchanged vs. the prior period. May carry NEW (first time we’ve seen them).',
      },
      {
        h: '🟡 Yellow',
        d: 'Present, but something changed: COMMISSION_CHANGED (the amount moved), DATA_CHANGED (a tracked field changed), or REAPPEARED (back after a gap — with a delta flag if the commission differs).',
      },
      {
        h: '🔴 Red',
        d: 'Was present last period, now absent — DROPPED. We show the last statement they appeared on.',
      },
      {
        h: 'Attrition',
        d: 'Red members ÷ members present the prior period — your churn rate for the period.',
      },
    ],
  },
  {
    id: 'support',
    title: 'More help',
    blurb: 'Still stuck, or need a carrier onboarded?',
    points: [
      {
        h: 'Onboarding a carrier',
        d: 'Send us a sample statement and we draft the mapping and activate it for you — usually same day. You don’t configure anything technical.',
      },
      {
        h: 'Contact',
        d: 'Reach the CommissionSight team and we’ll help — including fixing a mapping, a rate schedule, or a statement that looks off.',
      },
    ],
  },
];

export function HelpDrawer({
  open,
  onClose,
  section,
}: {
  open: boolean;
  onClose: () => void;
  section?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !section) return;
    const t = setTimeout(() => {
      document.getElementById(`help-${section}`)?.scrollIntoView({ block: 'start' });
    }, 60);
    return () => clearTimeout(t);
  }, [open, section]);

  // Render into <body> via a portal: the drawer is mounted inside the sticky
  // header, which uses backdrop-blur (a `backdrop-filter`). That establishes a
  // containing block for `position: fixed` descendants, so without a portal the
  // drawer would size/position relative to the short header bar instead of the
  // viewport — making it not full-height. Portaling to body fixes it to the screen.
  return createPortal(
    <>
      <button
        type="button"
        aria-label="Close help"
        tabIndex={-1}
        onClick={onClose}
        className={`fixed inset-0 z-40 cursor-default bg-neutral-900/50 transition-opacity ${open ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      />
      <aside
        aria-label="Help"
        className={`fixed inset-y-0 left-0 z-50 flex w-full max-w-md flex-col bg-white shadow-2xl transition-transform duration-300 dark:bg-neutral-900 ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4 dark:border-neutral-800">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600 text-xs font-bold text-white">
              ?
            </span>
            Help &amp; guide
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-white"
            aria-label="Close help"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <nav className="flex flex-wrap gap-1.5 border-b border-neutral-100 px-5 py-3 dark:border-neutral-800">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#help-${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(`help-${s.id}`)?.scrollIntoView({ block: 'start' });
              }}
              className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
            >
              {s.title.split(/[ —]/)[0]}
            </a>
          ))}
        </nav>
        <div className="flex-1 space-y-7 overflow-y-auto px-5 py-5">
          {SECTIONS.map((s) => (
            <section key={s.id} id={`help-${s.id}`} className="scroll-mt-2">
              <h3 className="text-sm font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                {s.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
                {s.blurb}
              </p>
              <dl className="mt-3 space-y-2.5">
                {s.points.map((p) => (
                  <div
                    key={p.h}
                    className="rounded-lg border border-neutral-200/70 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-800/40"
                  >
                    <dt className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                      {p.h}
                    </dt>
                    <dd className="mt-0.5 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
                      {p.d}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </aside>
    </>,
    document.body,
  );
}
